import styled, { css, DefaultTheme } from 'styled-components'

import { TextFieldProps } from '.'

type IconProps = Pick<
  TextFieldProps,
  | 'icon'
  | 'iconColor'
  | 'iconBgColor'
  | 'iconPosition'
  | 'iconIsButton'
  | 'iconPosition'
  | 'keepStyleOnIconIsButton'
  | 'radius'
  | 'height'
>

type ClearButtonProps = Pick<TextFieldProps, 'radius' | 'height'>

type InputProps = Pick<
  TextFieldProps,
  | 'icon'
  | 'iconPosition'
  | 'bgColor'
  | 'radius'
  | 'baselineStyle'
  | 'clearable'
  | 'height'
>

type WrapperProps = Pick<TextFieldProps, 'disabled' | 'baselineStyle'> & {
  error?: boolean
}

type AsteriskProps = Pick<TextFieldProps, 'requiredColor'>

export const LabelWrapper = styled.div`
  position: relative;
  display: flex;
`

export const Label = styled.label<TextFieldProps>`
  ${({
    theme,
    disabled,
    labelColor,
    labelSize,
    labelWeight,
    labelOverlapping,
    baselineStyle
  }) => css`
    color: ${theme.ds.colors[labelColor!]};
    font-size: ${theme.ds.typography.size?.[labelSize!]};
    font-weight: ${theme.ds.typography.weight?.[labelWeight!]};
    line-height: ${theme.ds.typography.lineHeight.medium};
    cursor: pointer;
    margin-bottom: 8px;

    ${labelOverlapping &&
    css`
      position: absolute;
      z-index: 10;
      top: 0;
      left: 14px;
      transform: translateY(-50%);
      margin-bottom: 0px;
      padding: 0 2px;
      line-height: 1;
      background-color: ${disabled
        ? theme.ds.colors.neutral25
        : theme.ds.colors.white};
    `}

    ${labelOverlapping &&
    baselineStyle &&
    css`
      position: relative;
      left: 0;
      padding: 0;
      transform: unset;
      background-color: ${theme.ds.colors.transparent};
    `}
  `}
`

export const LabelIcon = styled.div`
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
`

export const Asterisk = styled.span<AsteriskProps>`
  ${({ theme, requiredColor }) => css`
    font-size: ${theme.ds.typography.size.small};
    font-weight: ${theme.ds.typography.weight.normal};
    color: ${theme.ds.colors[requiredColor!]};
    cursor: pointer;
  `}
`

export const InputWrapper = styled.div<WrapperProps>`
  position: relative;
  display: flex;
  align-items: center;
  ${({ theme, baselineStyle }) =>
    wrapperModifiers['normal'](theme, baselineStyle)}
`

export const Input = styled.input<InputProps>`
  ${({
    theme,
    icon,
    iconPosition,
    bgColor,
    radius,
    baselineStyle,
    clearable,
    height
  }) => css`
    all: unset;
    width: 100%;
    height: ${height || '48px'};
    padding-right: ${!!icon && iconPosition === 'insideRight'
      ? '24px'
      : clearable
      ? '56px'
      : '16px'};
    padding-left: ${!!icon && iconPosition === 'insideLeft' ? '40px' : '16px'};
    color: ${theme.ds.colors.neutral900};
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size.small};
    line-height: ${theme.ds.typography.lineHeight.medium};
    background-color: ${theme.ds.colors[bgColor!]};
    border: 1px solid ${theme.ds.colors.neutral300};
    outline: none;
    text-align: left;

    ${baselineStyle
      ? css`
          border-top-color: transparent;
          border-right-color: transparent;
          border-left-color: transparent;
          padding: 0;
        `
      : css`
          border-radius: ${theme.ds.typography.border.radius[radius!]};
        `}

    &::placeholder {
      color: ${theme.ds.colors.neutral400};
      font-weight: ${theme.ds.typography.weight.normal};
      opacity: 1;
      text-align: left;
    }

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      opacity: 1;
    }
  `}
`

export const Icon = styled.button<IconProps>`
  ${({
    theme,
    iconPosition,
    iconIsButton,
    iconBgColor,
    disabled,
    keepStyleOnIconIsButton,
    radius,
    height
  }) => {
    const inside =
      iconPosition === 'insideRight' || iconPosition === 'insideLeft'
    const iconLeft =
      iconPosition === 'insideLeft' || iconPosition === 'outsideLeft'

    return css`
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      order: ${iconLeft ? 0 : 1};
      height: ${height || '48px'};
      width: ${height || '48px'};
      position: ${inside ? 'absolute' : 'relative'} !important;
      top: ${inside ? '50%' : 'unset'};
      right: ${iconPosition === 'insideRight' ? 0 : 'unset'};
      left: ${iconPosition === 'insideLeft' ? 0 : 'unset'};
      transform: ${inside ? 'translateY(-50%)' : 'unset'};
      cursor: ${(iconIsButton || keepStyleOnIconIsButton) && !disabled
        ? 'pointer'
        : 'default'};
      outline: none;
      color: ${iconIsButton && iconBgColor && inside && !keepStyleOnIconIsButton
        ? theme.ds.colors.white
        : theme.ds.colors.neutral300};
      background: ${iconIsButton &&
      disabled &&
      inside &&
      !keepStyleOnIconIsButton
        ? theme.ds.colors.primary400
        : iconIsButton && iconBgColor && inside && !keepStyleOnIconIsButton
        ? theme.ds.colors[iconBgColor!]
        : 'none'};
      border-top: ${iconIsButton && inside && !keepStyleOnIconIsButton
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-right: ${iconIsButton &&
      iconPosition === 'insideRight' &&
      !keepStyleOnIconIsButton
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-bottom: ${iconIsButton && inside && !keepStyleOnIconIsButton
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-left: ${iconIsButton &&
      iconPosition === 'insideLeft' &&
      !keepStyleOnIconIsButton
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-top-right-radius: ${iconPosition === 'insideRight'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-bottom-right-radius: ${iconPosition === 'insideRight'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-top-left-radius: ${iconPosition === 'insideLeft'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-bottom-left-radius: ${iconPosition === 'insideLeft'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      transition: all ease 0.2s;

      svg,
      path {
        max-width: 24px;
        max-height: 24px;
        fill: ${iconIsButton &&
        iconBgColor &&
        inside &&
        !keepStyleOnIconIsButton
          ? theme.ds.colors.white
          : theme.ds.colors.neutral300};
        opacity: 1;
      }

      &:hover {
        opacity: ${iconIsButton && !disabled ? 0.8 : 1};
      }
    `
  }}
`

export const ClearButton = styled.button<ClearButtonProps>`
  ${({ theme, height }) => css`
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${height || '48px'};
    width: ${height || '48px'};
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    cursor: pointer;
    outline: none;
    background: none;
    border: none;
    color: ${theme.ds.colors.neutral500};

    &:hover {
      color: ${theme.ds.colors.neutral700};
    }

    &:disabled {
      cursor: not-allowed;
      color: ${theme.ds.colors.neutral300};
    }

    svg,
    path {
      max-width: 20px;
      max-height: 20px;
      fill: currentColor;
      opacity: 1;
    }
  `}
`

export const Error = styled.p`
  margin-top: 4px;
  color: ${({ theme }) => theme.ds.colors.danger300};
  font-weight: ${({ theme }) => theme.ds.typography.weight.normal};
  line-height: ${({ theme }) => theme.ds.typography.lineHeight.medium};
  font-size: ${({ theme }) => theme.ds.typography.size.xxsmall};
`

const wrapperModifiers = {
  normal: (theme: DefaultTheme, baselineStyle?: boolean) => css`
    ${Input} {
      &:focus {
        border-color: ${baselineStyle
          ? 'transparent'
          : theme.ds.colors.primary500};
        border-bottom-color: ${theme.ds.colors.primary500};
      }
    }
  `,
  error: (theme: DefaultTheme) => css`
    ${Input} {
      border-color: ${theme.ds.colors.danger200} !important;
    }
    ${Icon} {
      color: ${theme.ds.colors.danger200} !important;
      border-color: ${theme.ds.colors.danger200} !important;
    }
  `,
  disabled: (theme: DefaultTheme, baselineStyle?: boolean) => css`
    ${Input} {
      cursor: not-allowed;
      color: ${theme.ds.colors.neutral300};
      border-color: ${baselineStyle
        ? 'transparent'
        : theme.ds.colors.neutral300};
      border-bottom-color: ${theme.ds.colors.neutral300};
      background-color: ${baselineStyle
        ? theme.ds.colors.transparent
        : theme.ds.colors.neutral50};
    }
  `
}

export const Wrapper = styled.div<WrapperProps>`
  ${({ theme, error }) => error && wrapperModifiers.error(theme)}
  ${({ theme, disabled, baselineStyle }) =>
    disabled && wrapperModifiers.disabled(theme, baselineStyle)}
`
